import React from 'react';
import { Theme } from '../contexts/ThemeContext';

const MARKDOWN_LINK_REGEX = /\[([^\]]+)\]\((https?:\/\/[^\s)]+|\/[^\s)]+)\)/g;
const LINK_REGEX = /(https?:\/\/[^\s<]+|\/[a-zA-Z0-9\-._~:/?#[\]@!$&'()*+,;=%]*)/g;
const ABSOLUTE_URL_ONLY_REGEX = /^https?:\/\/[^\s<]+$/;

const getDefaultLinkStyle = (theme: Theme, isAbsoluteUrl: boolean): React.CSSProperties => {
    if (isAbsoluteUrl) {
        return { textDecoration: 'underline' };
    }

    return {
        display: 'inline-block',
        textDecoration: 'none',
        backgroundColor: theme.colors.secondary,
        color: theme.colors.text.inverse,
        borderRadius: theme.borderRadius.sm,
        padding: `2px ${theme.spacing.xs}`,
        fontWeight: theme.typography.fontWeight.medium,
    };
};

export const renderTextWithLinks = (
    content: string,
    navigate: (url: string) => void,
    theme: Theme,
    getLinkStyle: (isAbsoluteUrl: boolean) => React.CSSProperties = (isAbsoluteUrl) =>
        getDefaultLinkStyle(theme, isAbsoluteUrl),
): React.ReactNode => {
    const lines = content.split('\n');

    const renderLineWithLinks = (line: string, lineIndex: number): React.ReactNode[] => {
        const parts: React.ReactNode[] = [];
        let cursor = 0;
        let partIndex = 0;

        for (const markdownMatch of line.matchAll(MARKDOWN_LINK_REGEX)) {
            const fullMatch = markdownMatch[0];
            const text = markdownMatch[1];
            const url = markdownMatch[2];
            const start = markdownMatch.index ?? 0;

            if (start > cursor) {
                const preMarkdown = line.slice(cursor, start);
                const plainAndUrls = preMarkdown.split(LINK_REGEX);
                for (const segment of plainAndUrls) {
                    if (!segment) {
                        continue;
                    }
                    if (LINK_REGEX.test(segment)) {
                        const isAbsoluteUrl = ABSOLUTE_URL_ONLY_REGEX.test(segment);
                        parts.push(
                            <a
                                key={`link-${lineIndex}-${partIndex++}`}
                                href={segment}
                                target={isAbsoluteUrl ? '_blank' : undefined}
                                rel={isAbsoluteUrl ? 'noopener noreferrer' : undefined}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (!isAbsoluteUrl) {
                                        e.preventDefault();
                                        navigate(segment);
                                    }
                                }}
                                style={getLinkStyle(isAbsoluteUrl)}
                            >
                                {segment}
                            </a>
                        );
                    } else {
                        parts.push(<React.Fragment key={`text-${lineIndex}-${partIndex++}`}>{segment}</React.Fragment>);
                    }
                    LINK_REGEX.lastIndex = 0;
                }
            }

            const isAbsoluteUrl = ABSOLUTE_URL_ONLY_REGEX.test(url);
            parts.push(
                <a
                    key={`md-${lineIndex}-${partIndex++}`}
                    href={url}
                    target={isAbsoluteUrl ? '_blank' : undefined}
                    rel={isAbsoluteUrl ? 'noopener noreferrer' : undefined}
                    onClick={(e) => {
                        e.stopPropagation();
                        if (!isAbsoluteUrl) {
                            e.preventDefault();
                            navigate(url);
                        }
                    }}
                    style={getLinkStyle(isAbsoluteUrl)}
                >
                    {text}
                </a>
            );

            cursor = start + fullMatch.length;
        }

        if (cursor < line.length) {
            const tail = line.slice(cursor);
            const plainAndUrls = tail.split(LINK_REGEX);
            for (const segment of plainAndUrls) {
                if (!segment) {
                    continue;
                }
                if (LINK_REGEX.test(segment)) {
                    const isAbsoluteUrl = ABSOLUTE_URL_ONLY_REGEX.test(segment);
                    parts.push(
                        <a
                            key={`tail-link-${lineIndex}-${partIndex++}`}
                            href={segment}
                            target={isAbsoluteUrl ? '_blank' : undefined}
                            rel={isAbsoluteUrl ? 'noopener noreferrer' : undefined}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (!isAbsoluteUrl) {
                                    e.preventDefault();
                                    navigate(segment);
                                }
                            }}
                            style={getLinkStyle(isAbsoluteUrl)}
                        >
                            {segment}
                        </a>
                    );
                } else {
                    parts.push(<React.Fragment key={`tail-text-${lineIndex}-${partIndex++}`}>{segment}</React.Fragment>);
                }
                LINK_REGEX.lastIndex = 0;
            }
        }

        return parts.length > 0 ? parts : [<React.Fragment key={`empty-${lineIndex}`}></React.Fragment>];
    };

    return lines.map((line, index) => (
        <React.Fragment key={`line-${index}`}>
            {renderLineWithLinks(line, index)}
            {index < lines.length - 1 && <br />}
        </React.Fragment>
    ));
};
